import { CSSProperties, Fragment, ReactNode, RefObject, useRef } from 'react'

import {
  DEFAULT_LINK_COLOR,
  DEFAULT_LINK_STYLE,
  LinkStyle,
  MEASURED_LINK_STYLES,
} from './linked-columns.constants'
import * as S from './linked-columns.styled'
import { buildCurvePaths, buildLinkShape, buildRailShape } from './linked-columns.utils'
import { LinkMeasure, useLinkMeasure } from './use-link-measure'

interface Link {
  from: string
  to: string
}

interface LinkedColumnsProps {
  children: ReactNode
  dimmed?: boolean
  linkColor?: string
  linkStyle?: LinkStyle
  links: Link[]
}

const DOT_RADIUS = 3
const RAIL_OFFSET = 4

const getIndexStyle = (index: number) => ({ '--index': index }) as CSSProperties

const Dots = ({ points }: { points: { x: number; y: number }[] }) =>
  points.map(({ x, y }, index) => (
    <S.Fading key={index} style={getIndexStyle(index)}>
      <circle cx={x} cy={y} r={DOT_RADIUS} />
    </S.Fading>
  ))

const BracketShape = ({ measure }: { measure: LinkMeasure }) => {
  const shape = buildLinkShape(measure)
  if (!shape) return null

  return (
    <>
      <S.Spine d={shape.path} pathLength={1} />
      <S.Fading>
        <circle cx={shape.origin.x} cy={shape.origin.y} r={DOT_RADIUS} />
      </S.Fading>
      {shape.arms.map((arm, index) => (
        <S.Fading key={index} style={getIndexStyle(index)}>
          <path d={arm} />
        </S.Fading>
      ))}
      <Dots points={shape.dots} />
    </>
  )
}

const RailShape = ({ measure }: { measure: LinkMeasure }) => {
  const shape = buildRailShape({
    children: measure.children,
    parent: measure.parent,
    railX: measure.toPanelLeft - RAIL_OFFSET,
  })
  if (!shape) return null

  return (
    <>
      <S.Spine d={shape.path} pathLength={1} />
      <S.Fading>
        <circle cx={shape.origin.x} cy={shape.origin.y} r={DOT_RADIUS} />
      </S.Fading>
      {shape.arms.map((arm, index) => (
        <S.Fading key={index} style={getIndexStyle(index)}>
          <path d={arm} />
        </S.Fading>
      ))}
    </>
  )
}

const CurvesShape = ({ measure }: { measure: LinkMeasure }) => (
  <>
    <S.Fading>
      <circle cx={measure.parent.x} cy={measure.parent.y} r={DOT_RADIUS} />
    </S.Fading>
    {buildCurvePaths(measure.parent, measure.children).map((path, index) => (
      <S.Fading key={index} style={getIndexStyle(index)}>
        <path d={path} />
      </S.Fading>
    ))}
    <Dots points={measure.children} />
  </>
)

const LinkConnector = ({
  containerRef,
  dimmed,
  from,
  linkColor,
  linkStyle,
  to,
}: Link & {
  containerRef: RefObject<HTMLElement | null>
  dimmed: boolean
  linkColor: string
  linkStyle: LinkStyle
}) => {
  const measure = useLinkMeasure(containerRef, from, to)
  if (!measure) return null

  return (
    <S.Overlay aria-hidden $color={linkColor} $dimmed={dimmed}>
      <Fragment key={measure.parentKey}>
        {linkStyle === 'bracket' && <BracketShape measure={measure} />}
        {linkStyle === 'rail' && <RailShape measure={measure} />}
        {linkStyle === 'curves' && <CurvesShape measure={measure} />}
      </Fragment>
    </S.Overlay>
  )
}

export const LinkedColumns = ({
  children,
  dimmed = false,
  linkColor = DEFAULT_LINK_COLOR,
  linkStyle = DEFAULT_LINK_STYLE,
  links,
}: LinkedColumnsProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  return (
    <S.Columns
      ref={containerRef}
      $fromColumns={links.map(({ from }) => from)}
      $linkColor={linkColor}
      $linkStyle={linkStyle}
    >
      {children}
      {MEASURED_LINK_STYLES.includes(linkStyle) &&
        links.map((link) => (
          <LinkConnector
            key={`${link.from}-${link.to}-${linkStyle}`}
            containerRef={containerRef}
            dimmed={dimmed}
            linkColor={linkColor}
            linkStyle={linkStyle}
            {...link}
          />
        ))}
    </S.Columns>
  )
}
